import React, { useEffect, useState } from 'react';
import { GlassCard } from '../components/GlassCard';
import { ProgressBar } from '../components/ProgressBar';
import { CalendarIcon, InfoIcon, SendIcon, DownloadIcon, ClockIcon, SparklesIcon } from 'lucide-react';
export function Home() {
  // Mock user data
  const user = {
    name: 'Finn',
    auraBalance: 450000,
    level: 12,
    progress: 65,
    challengesLeft: 3
  };
  // Mock trend data
  const trendPercentage = 12.5;
  // Animation state for count-up effect
  const [displayBalance, setDisplayBalance] = useState(0);
  // Count-up animation effect
  useEffect(() => {
    const duration = 1500; // 1.5 seconds
    const frameRate = 30; // frames per second
    const totalFrames = duration / 1000 * frameRate;
    const increment = user.auraBalance / totalFrames;
    let currentFrame = 0;
    let currentBalance = 0;
    const timer = setInterval(() => {
      currentFrame++;
      currentBalance += increment;
      if (currentFrame <= totalFrames) {
        setDisplayBalance(Math.floor(currentBalance));
      } else {
        setDisplayBalance(user.auraBalance);
        clearInterval(timer);
      }
    }, 1000 / frameRate);
    return () => clearInterval(timer);
  }, [user.auraBalance]);
  // Upcoming events
  const upcomingEvents = [{
    id: '1',
    title: 'Pepsi Summer Challenge',
    date: 'Jun 15, 2023',
    reward: 200000
  }, {
    id: '2',
    title: 'Nike Fitness Week',
    date: 'Jun 22, 2023',
    reward: 350000
  }];
  return <div className="space-y-5 pb-20 px-4 pt-4" data-id="element-112">
      {/* Welcome header */}
      <div className="flex justify-between items-center mb-2" data-id="element-113">
        <h1 className="text-xl font-bold text-gray-900" data-id="element-114">
          Good day, {user.name}.
        </h1>
      </div>
      {/* Balance card - Fixed layout without sparkline graph */}
      <div className="bg-gradient-to-br from-white to-blue-50/5 rounded-2xl shadow-[0_4px_16px_rgba(0,0,0,0.05),0_1px_2px_rgba(0,0,0,0.03)] backdrop-blur-xl bg-white/85 border border-white/90 p-5 relative overflow-hidden" data-id="element-115">
        <div className="flex justify-between" data-id="element-116">
          <div className="flex flex-col" data-id="element-117">
            <span className="text-blue-500 text-xs font-medium uppercase tracking-wider" data-id="element-118">
              Your Balance
            </span>
            {/* Subtle divider */}
            <div className="w-14 h-[1px] bg-gradient-to-r from-blue-100 to-transparent my-2" data-id="element-119"></div>
            <div className="flex items-baseline" data-id="element-120">
              <div className="relative" data-id="element-121">
                <span className="text-[42px] font-extrabold text-gray-900 tabular-nums leading-none" data-id="element-122">
                  {displayBalance.toLocaleString()}
                </span>
                <div className="absolute -right-1 -top-1" data-id="element-123">
                  <SparklesIcon size={12} className="text-blue-400 animate-pulse opacity-70" data-id="element-124" />
                </div>
              </div>
              <div className="relative ml-2" data-id="element-125">
                <span className="text-[16px] font-medium text-blue-500" data-id="element-126">
                  $AURA
                </span>
                <div className="absolute -top-1 -right-2 w-6 h-6 bg-blue-500/5 rounded-full blur-md animate-pulse" data-id="element-127"></div>
              </div>
            </div>
            {/* Quick action buttons - glass morphism style */}
            <div className="flex space-x-2 mt-4" data-id="element-128">
              <button className="flex items-center justify-center px-4 py-2 bg-white/40 backdrop-blur-md border border-white/50 rounded-full text-blue-600 text-sm font-medium transition-colors h-[44px] shadow-sm hover:bg-white/50" data-id="element-129">
                <SendIcon size={16} className="mr-1" data-id="element-130" />
                Send
              </button>
              <button className="flex items-center justify-center px-4 py-2 bg-white/40 backdrop-blur-md border border-white/50 rounded-full text-blue-600 text-sm font-medium transition-colors h-[44px] shadow-sm hover:bg-white/50" data-id="element-131">
                <DownloadIcon size={16} className="mr-1" data-id="element-132" />
                Receive
              </button>
              <button className="flex items-center justify-center px-4 py-2 bg-white/40 backdrop-blur-md border border-white/50 rounded-full text-blue-600 text-sm font-medium transition-colors h-[44px] shadow-sm hover:bg-white/50" data-id="element-133">
                <ClockIcon size={16} className="mr-1" data-id="element-134" />
                History
              </button>
            </div>
          </div>
          <div className="flex flex-col items-end justify-between" data-id="element-135">
            {/* Trend indicator - repositioned */}
            <div className="flex flex-col items-end mb-2" data-id="element-136">
              <div className="flex items-center px-2.5 py-1 bg-green-50 rounded-full animate-pulse-subtle" data-id="element-137">
                <span className="text-green-600 font-medium text-xs" data-id="element-138">
                  ↑ {trendPercentage}%
                </span>
              </div>
              <span className="text-xs text-gray-500 mt-0.5" data-id="element-139">This week</span>
            </div>
            {/* Info icon - repositioned with glass effect */}
            <button className="w-10 h-10 rounded-full bg-white/40 backdrop-blur-md border border-white/50 flex items-center justify-center text-blue-500 transition-colors shadow-sm hover:bg-white/50" data-id="element-140">
              <InfoIcon size={16} data-id="element-141" />
            </button>
          </div>
        </div>
        {/* Subtle pattern overlay - kept as is */}
        <div className="absolute inset-0 rounded-2xl pointer-events-none overflow-hidden z-0" data-id="element-142">
          <div className="w-full h-full opacity-[0.02] bg-[radial-gradient(circle,_rgba(59,130,246,0.4)_0.8px,_transparent_0.8px)] bg-[length:10px_10px]" data-id="element-143"></div>
        </div>
      </div>
      {/* Level card - consistent spacing */}
      <div className="bg-white rounded-2xl shadow-md p-4" data-id="element-144">
        <div className="flex flex-col" data-id="element-145">
          <div className="flex justify-between items-center" data-id="element-146">
            <span className="text-blue-500 text-sm font-medium uppercase tracking-wide" data-id="element-147">
              Aura Level
            </span>
            <span className="text-2xl font-bold text-gray-900" data-id="element-148">
              {user.level}
            </span>
          </div>
          <ProgressBar progress={user.progress} className="mt-4 mb-2" data-id="element-149" />
          <div className="text-sm text-gray-500" data-id="element-150">
            {user.challengesLeft} more challenges to level {user.level + 1}
          </div>
        </div>
      </div>
      {/* Upcoming events - consistent spacing */}
      <div data-id="element-151">
        <h2 className="text-lg font-bold text-gray-900 mb-3" data-id="element-152">
          Upcoming Events
        </h2>
        <div className="space-y-3" data-id="element-153">
          {upcomingEvents.map(event => <div key={event.id} className="bg-white rounded-2xl shadow-md p-4" data-id="element-154">
              <div className="flex justify-between items-center" data-id="element-155">
                <div data-id="element-156">
                  <h3 className="font-semibold text-base text-gray-900" data-id="element-157">
                    {event.title}
                  </h3>
                  <div className="flex items-center mt-1 text-sm text-gray-500" data-id="element-158">
                    <CalendarIcon size={14} className="mr-1.5" data-id="element-159" />
                    {event.date}
                  </div>
                </div>
                <div className="text-right" data-id="element-160">
                  <div className="text-xs text-blue-500 font-medium uppercase tracking-wide" data-id="element-161">
                    Reward
                  </div>
                  <div className="font-medium text-base" data-id="element-162">
                    {event.reward.toLocaleString()} $AURA
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </div>;
}